import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { WS_INVALIDATED_QUERY_STALE_TIME } from '../constants';
import { SchemaWithdrawDto } from '../generated/apiTypes';
import { fetchClient } from '../lib/apiClient';
import { Gwei } from '../lib/gwei';
import { getInitiateWithdrawKey } from './useInitiateWithdraw';

// Polling interval for withdrawals that don't have readyAt yet
const REFETCH_INTERVAL = 2000;

const transformPendingWithdraw = (data: SchemaWithdrawDto) => ({
  ...data,
  amount: Gwei.toGwei(data.amount),
});

export type PendingWithdraw = ReturnType<typeof transformPendingWithdraw>;

export const getPendingWithdrawQuery = (subaccountId: string | undefined) =>
  queryOptions({
    queryKey: ['subaccount', 'withdraw', { subaccountId }] as const,
    enabled: Boolean(subaccountId),
    staleTime: WS_INVALIDATED_QUERY_STALE_TIME,
    queryFn: async ({ signal }) => {
      if (!subaccountId) return [];
      const result = await fetchClient.GET('/v1/token/withdraw', {
        signal,
        params: { query: { subaccountId, active: true } },
      });
      const pendingWithdraws = result.data?.data;
      if (pendingWithdraws) {
        return pendingWithdraws.map(transformPendingWithdraw);
      }

      throw result.error;
    },
  });

export const usePendingWithdraw = (subaccountId: string | undefined) => {
  const queryClient = useQueryClient();

  const resetInitiateWithdrawMutation = useCallback(() => {
    const mutationKey = getInitiateWithdrawKey(subaccountId);
    const mutations = queryClient.getMutationCache().findAll({
      mutationKey,
      exact: true,
    });
    mutations.forEach((mutation) => {
      queryClient.getMutationCache().remove(mutation);
      queryClient.getMutationCache().notify({ type: 'removed', mutation });
    });
  }, [queryClient, subaccountId]);

  const query = useQuery({
    ...getPendingWithdrawQuery(subaccountId),
    refetchInterval: (query) => {
      const data = query.state.data;
      // Poll only when withdrawal exists without readyAt timestamp
      return data?.some((w) => w.status === 'PENDING' && !w.readyAt) ? REFETCH_INTERVAL : false;
    },
  });

  useEffect(() => {
    // Only reset mutation when we have a withdrawal with readyAt (fully processed)
    const hasCompleteWithdrawal = query.data?.some((w) => w.readyAt) ?? false;
    if (hasCompleteWithdrawal) {
      resetInitiateWithdrawMutation();
    }
  }, [query.data, resetInitiateWithdrawMutation]);

  return query;
};
